// Protected Route Component
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({ children, requiredRole = null, allowedRoles = [] }) => {
  const { currentUser, userProfile, loading } = useAuth();
  const location = useLocation();

  // Debug logs
  console.log('[ProtectedRoute] loading:', loading);
  console.log('[ProtectedRoute] currentUser:', currentUser);
  console.log('[ProtectedRoute] userProfile:', userProfile);
  console.log('[ProtectedRoute] requiredRole:', requiredRole);
  console.log('[ProtectedRoute] allowedRoles:', allowedRoles);

  if (loading) {
    console.log('[ProtectedRoute] Loading state, rendering spinner');
    return (
      <div className="loading-container" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div className="spinner" style={{
          border: '4px solid #f3f4f6',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading...</p>
      </div>
    );
  }

  if (!currentUser) {
    console.log('[ProtectedRoute] No currentUser, redirecting to /login');
    // Redirect to login page with return URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userRole = userProfile?.role;

  // Check if role matches required role (if specified)
  if (requiredRole && userRole !== requiredRole) {
    console.log('[ProtectedRoute] Access denied. Required:', requiredRole, 'Actual:', userRole);
    
    // Redirect to user's correct dashboard instead of showing error
    const correctDashboard = getCorrectDashboard(userRole);
    return <Navigate to={correctDashboard} replace />;
  }

  // Check if role is in allowed roles (if specified)
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    console.log('[ProtectedRoute] Access denied. Allowed roles:', allowedRoles, 'Actual:', userRole);
    
    // Redirect to user's correct dashboard
    const correctDashboard = getCorrectDashboard(userRole);
    return <Navigate to={correctDashboard} replace />;
  }

  console.log('[ProtectedRoute] Access granted. Rendering children.');
  return children;
};

// Helper function to get correct dashboard path based on role
const getCorrectDashboard = (role) => {
  switch (role) {
    case 'admin':
      return '/admin';
    case 'manager':
      return '/manager';
    case 'owner':
      return '/owner';
    case 'guest':
    default:
      return '/guest/dashboard';
  }
};

export default ProtectedRoute;